/**
 * Web 前端性能监控
 *
 * 模块说明：
 * 本模块用于 **Web 管理后台前端** 的性能监控和上报。
 *
 * ⚠️ 注意区分：
 * - 本模块 (performance-monitor.ts)：Web 前端性能上报
 *   API 路径：POST /api/v1/web-logs/performance
 *   数据存储：mes_web_performance_logs 表
 *   查看界面：/system/web-monitoring/performance
 *
 * - App 端性能上报：移动 App 的性能上报（iOS/Android/小程序）
 *   API 路径：POST /api/v1/app-logs/performance
 *   数据存储：mes_app_performance_logs 表
 *   查看界面：/system/app-monitoring/performance
 *
 * 功能：
 * 1. 页面加载性能指标收集
 * 2. Core Web Vitals (LCP, INP, CLS) 监控（官方 web-vitals 库）
 * 3. 长任务监控
 * 4. 资源加载性能
 * 5. 性能数据上报
 *
 * @see api/modules/web-logs.ts - Web 前端日志 API
 * @see modules/mes/web-monitoring/ - Web 日志查看界面
 * @author YSFL Team
 * @date 2026-01-23
 * @updated 2026-01-24 优化 API 路径命名（/web/ -> /web-logs/）
 */
import type {
  BatchUploadWebPerformanceForm,
  WebMetricType,
  WebPerformanceLogForm,
} from '#/api/modules/web-logs';

import { useUserStore } from '@vben/stores';

import { onCLS, onINP, onLCP } from 'web-vitals';

import { webLogsApi } from '#/api/modules/web-logs';
import { getDeviceId, getSessionId } from '#/utils/device';
import { updateConfigFromResponse } from '#/utils/monitoring-config';

/**
 * 格式化本地时间为 YYYY-MM-DD HH:mm:ss 格式
 * 避免 ISO 8601 的时区问题
 */
function formatLocalDateTime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function safePageUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

function pageQueryKeys(): string[] {
  return [...new Set(new URLSearchParams(window.location.search).keys())];
}

/**
 * 获取当前用户信息
 */
function getCurrentUserInfo(): {
  realName: null | string;
  userId: null | number;
  username: null | string;
} {
  try {
    const userStore = useUserStore();
    const userInfo = userStore.userInfo;
    return {
      userId: userInfo?.id || null,
      username: userInfo?.username || null,
      realName: userInfo?.realName || null,
    };
  } catch {
    return { userId: null, username: null, realName: null };
  }
}

/**
 * 检查用户是否已登录
 */
function isUserLoggedIn(): boolean {
  try {
    const userStore = useUserStore();
    return !!userStore.userInfo?.id;
  } catch {
    return false;
  }
}

// 配置
interface PerformanceMonitorOptions {
  /** 是否启用 */
  enabled?: boolean;
  /** 是否在控制台输出 */
  showInConsole?: boolean;
  /** 采样率 (0-1) */
  sampleRate?: number;
  /** 慢请求阈值（毫秒） */
  slowThreshold?: number;
}

const defaultOptions: Required<PerformanceMonitorOptions> = {
  enabled: import.meta.env.PROD,
  showInConsole: import.meta.env.DEV,
  sampleRate: 0.01, // 1% 采样率（降低 CPU 占用）
  slowThreshold: 3000, // 3秒算慢
};

let options: Required<PerformanceMonitorOptions> = { ...defaultOptions };

// 防止重复初始化
let isInitialized = false;
let uploadCooldownUntil = 0;

/** 待上报日志硬上限：锁屏/长开页面下 LCP 等观察器持续推送，必须兜底防止内存无限增长 */
const MAX_PENDING_LOGS = 500;

/** 定时上报间隔：需大于 MIN_REPORT_INTERVAL(60s) 节流窗口，避免定时触发被节流吞掉 */
const REPORT_INTERVAL = 90_000;

/** 待上报队列：满时丢弃最旧的日志 */
function enqueueLog(log: WebPerformanceLogForm): void {
  resumeReportTimerIfRecovered();
  pendingLogs.push(log);
  if (pendingLogs.length > MAX_PENDING_LOGS) {
    pendingLogs.splice(0, pendingLogs.length - MAX_PENDING_LOGS);
  }
}

/**
 * 判定认证类错误（与 behavior-tracker 口径一致）：
 * 登录失效后继续定时上报只会持续产生脏失败，应停止定时器并清空队列
 */
function isAuthError(error: any): boolean {
  if (error?.response?.status === 401) return true;
  if (error?.message?.includes('Unauthenticated')) return true;
  if (error?.response?.data?.message?.includes('Unauthenticated')) return true;
  return false;
}

let reportTimer: null | ReturnType<typeof setInterval> = null;

/** 认证失效停表标记：SPA 内重新登录后需要恢复周期上报，否则 401 一次即永久停摆 */
let reportTimerStoppedByAuth = false;

/** 停止定时上报并清空队列（认证失效或销毁时调用） */
function stopReportTimer(): void {
  if (reportTimer) {
    clearInterval(reportTimer);
    reportTimer = null;
  }
}

/** 认证失效停表后，用户重新登录时由首条日志入队触发恢复周期上报 */
function resumeReportTimerIfRecovered(): void {
  if (!reportTimerStoppedByAuth || !isUserLoggedIn()) {
    return;
  }
  reportTimerStoppedByAuth = false;
  stopReportTimer();
  reportTimer = setInterval(() => {
    reportMetrics();
  }, REPORT_INTERVAL);
}

function shouldSkipUpload(): boolean {
  return Date.now() < uploadCooldownUntil;
}

function markUploadLimited(error: any): void {
  if (error?.response?.status !== 429) {
    return;
  }

  const retryAfter = Number(error?.response?.headers?.['retry-after']);
  uploadCooldownUntil =
    Date.now() +
    (Number.isFinite(retryAfter) && retryAfter > 0
      ? retryAfter * 1000
      : 60_000);
}

// 待上报的性能日志
const pendingLogs: WebPerformanceLogForm[] = [];

// 缓存设备信息
let cachedDeviceId: null | string = null;
let cachedSessionId: null | string = null;

// CLS 累积值
let clsValue = 0;

// 长任务计数
let longTaskCount = 0;

// 上次上报时间（防止频繁上报）
let lastReportTime = 0;
const MIN_REPORT_INTERVAL = 60_000; // 最小上报间隔 60 秒（降低 CPU 占用）

/**
 * 获取设备ID（带缓存）
 */
async function getDeviceIdCached(): Promise<string> {
  if (!cachedDeviceId) {
    cachedDeviceId = await getDeviceId();
  }
  return cachedDeviceId;
}

/**
 * 获取会话ID（带缓存）
 */
function getSessionIdCached(): string {
  if (!cachedSessionId) {
    cachedSessionId = getSessionId();
  }
  return cachedSessionId;
}

/**
 * 获取浏览器信息
 */
function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

/**
 * 获取网络连接类型
 */
function getConnectionType(): string {
  const connection = (navigator as any).connection;
  if (connection) {
    return connection.effectiveType || connection.type || 'unknown';
  }
  return 'unknown';
}

/**
 * 创建性能日志
 */
async function createPerformanceLog(
  metricType: WebMetricType,
  metricName: string,
  metricValue: number,
  metricUnit: string,
  extra?: {
    category?: string;
    context?: Record<string, any>;
    isSlow?: boolean;
    threshold?: number;
  },
): Promise<WebPerformanceLogForm> {
  const deviceId = await getDeviceIdCached();
  const sessionId = getSessionIdCached();
  const userInfo = getCurrentUserInfo();

  // 构建增强的 context，包含用户详细信息（数据库没有 username 字段）
  const enhancedContext: Record<string, any> = {
    // 用户信息（方便调试）
    user: {
      username: userInfo.username,
      realName: userInfo.realName,
    },
    // 时区信息
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // 只记录查询参数名，避免把敏感查询值写入日志
    queryKeys: pageQueryKeys(),
    // 合并原有的 context
    ...extra?.context,
  };

  return {
    device_id: deviceId,
    session_id: sessionId,
    user_id: userInfo.userId,
    metric_type: metricType,
    metric_name: metricName,
    metric_value: metricValue,
    metric_unit: metricUnit,
    metric_category: extra?.category,
    page_url: safePageUrl(),
    page_route: window.location.pathname,
    is_slow: extra?.isSlow ?? metricValue > options.slowThreshold,
    threshold: extra?.threshold ?? options.slowThreshold,
    browser: getBrowserName(),
    connection_type: getConnectionType(),
    context: enhancedContext,
    // 使用本地时间格式（YYYY-MM-DD HH:mm:ss），后端已配置为 Asia/Shanghai 时区
    occurred_at: formatLocalDateTime(new Date()),
  };
}

/**
 * 收集导航计时指标
 */
async function collectNavigationTiming(): Promise<void> {
  const entries = performance.getEntriesByType(
    'navigation',
  ) as PerformanceNavigationTiming[];
  if (entries.length === 0) return;

  const [timing] = entries;
  if (!timing) return;

  const metrics = [
    { name: 'dns', value: timing.domainLookupEnd - timing.domainLookupStart },
    { name: 'tcp', value: timing.connectEnd - timing.connectStart },
    { name: 'ttfb', value: timing.responseStart - timing.requestStart },
    {
      name: 'dom_parse',
      value: timing.domComplete - timing.domInteractive,
    },
    {
      name: 'dom_content_loaded',
      value: timing.domContentLoadedEventEnd - timing.startTime,
    },
    { name: 'load_complete', value: timing.loadEventEnd - timing.startTime },
  ];

  for (const metric of metrics) {
    if (metric.value > 0) {
      const log = await createPerformanceLog(
        'page_load',
        metric.name,
        Math.round(metric.value),
        'ms',
        { category: 'navigation' },
      );
      enqueueLog(log);
    }
  }
}

/**
 * 收集绘制指标
 */
async function collectPaintTiming(): Promise<void> {
  const entries = performance.getEntriesByType('paint');

  for (const entry of entries) {
    const log = await createPerformanceLog(
      'page_load',
      entry.name === 'first-paint' ? 'fp' : 'fcp',
      Math.round(entry.startTime),
      'ms',
      { category: 'paint' },
    );
    enqueueLog(log);
  }
}

/**
 * 设置 Core Web Vitals 观察器（官方 web-vitals 库）
 *
 * 说明：替换早期手写的 LCP/FID/CLS PerformanceObserver 实现：
 * - 与 Google 规范实现对齐，自带 buffering 与跨浏览器兼容处理
 * - FID 已被 Web 界淘汰（web-vitals v5 起移除），改用 INP
 */
function setupWebVitalsObservers(): void {
  try {
    onLCP((metric) => {
      createPerformanceLog(
        'web_vitals',
        'LCP',
        Math.round(metric.value),
        'ms',
        { category: 'web_vitals', context: { rating: metric.rating } },
      ).then((log) => enqueueLog(log));
    });
  } catch {
    // 浏览器不支持
  }

  try {
    onINP((metric) => {
      createPerformanceLog(
        'web_vitals',
        'INP',
        Math.round(metric.value),
        'ms',
        { category: 'web_vitals', context: { rating: metric.rating } },
      ).then((log) => enqueueLog(log));
    });
  } catch {
    // 浏览器不支持
  }

  try {
    // CLS 是累积值：只更新累积变量，随定时上报一并刷出（避免每次偏移都入队）
    onCLS((metric) => {
      clsValue = metric.value;
    });
  } catch {
    // 浏览器不支持
  }
}

/**
 * 设置长任务观察器
 */
function setupLongTaskObserver(): void {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      longTaskCount += list.getEntries().length;
    });

    observer.observe({ type: 'longtask', buffered: true });
  } catch {
    // 浏览器不支持
  }
}

/**
 * 上报性能数据
 */
async function reportMetrics(): Promise<void> {
  // 检查用户登录状态（未登录时不上报，避免脏数据）
  if (!isUserLoggedIn()) {
    if (options.showInConsole) {
      // eslint-disable-next-line no-console
      console.info('[Performance] 用户未登录，跳过上报并清空缓存');
    }
    // 清空缓存，避免退出登录后积累大量无效日志
    pendingLogs.length = 0;
    return;
  }

  // 频率限制：防止频繁上报
  const now = Date.now();
  if (now - lastReportTime < MIN_REPORT_INTERVAL) {
    if (options.showInConsole) {
      // eslint-disable-next-line no-console
      console.info('[Performance] 上报间隔过短，跳过本次上报');
    }
    return;
  }
  lastReportTime = now;

  // 采样判断
  if (Math.random() > options.sampleRate) {
    if (options.showInConsole) {
      // eslint-disable-next-line no-console
      console.info('[Performance] 本次未采样');
    }
    return;
  }

  // 添加 CLS 和长任务统计
  if (clsValue > 0) {
    const clsLog = await createPerformanceLog(
      'web_vitals',
      'CLS',
      Math.round(clsValue * 1000) / 1000,
      '',
      { category: 'web_vitals' },
    );
    enqueueLog(clsLog);
  }

  if (longTaskCount > 0) {
    const longTaskLog = await createPerformanceLog(
      'page_load',
      'long_task_count',
      longTaskCount,
      'count',
      { category: 'performance' },
    );
    enqueueLog(longTaskLog);
  }

  // 控制台输出
  if (options.showInConsole) {
    // eslint-disable-next-line no-console
    console.info('[Performance] Web端性能指标', pendingLogs);
  }

  // 上报
  if (!options.enabled || pendingLogs.length === 0 || shouldSkipUpload()) {
    pendingLogs.length = 0;
    return;
  }

  try {
    const payload: BatchUploadWebPerformanceForm = {
      logs: pendingLogs.splice(0),
    };
    const response = await webLogsApi.batchUploadPerformance(payload);

    // 处理响应中的监控配置（增量更新，支持完整模式和精简模式）
    if (response?.monitoringConfig) {
      await updateConfigFromResponse(response.monitoringConfig);
    }
  } catch (error) {
    markUploadLimited(error);
    // 认证失效：停止定时上报并清空队列，避免锁屏/登录过期期间持续产生失败请求
    if (isAuthError(error)) {
      if (options.showInConsole) {
        console.warn('[Performance] 检测到认证失败，停止定时上报并清空队列');
      }
      reportTimerStoppedByAuth = true;
      stopReportTimer();
      pendingLogs.length = 0;
    }
    // 其他错误，上报失败，忽略
  }
}

/**
 * 初始化性能监控
 */
export function setupPerformanceMonitor(
  customOptions?: PerformanceMonitorOptions,
): void {
  // 防止重复初始化（热更新时会多次调用）
  if (isInitialized) {
    // eslint-disable-next-line no-console
    console.info('[PerformanceMonitor] 已初始化，跳过重复初始化');
    return;
  }

  options = { ...defaultOptions, ...customOptions };

  // 标记已初始化
  isInitialized = true;

  // 设置观察器
  setupWebVitalsObservers();
  setupLongTaskObserver();

  // 页面加载完成后收集指标
  if (document.readyState === 'complete') {
    setTimeout(async () => {
      await collectNavigationTiming();
      await collectPaintTiming();
      reportMetrics();
    }, 0);
  } else {
    window.addEventListener('load', () => {
      // 延迟收集，确保所有指标都已记录
      setTimeout(async () => {
        await collectNavigationTiming();
        await collectPaintTiming();
        reportMetrics();
      }, 3000);
    });
  }

  // 页面隐藏时上报最终的 CLS
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      reportMetrics();
    }
  });

  // 周期性上报：锁屏/长开不隐藏的页面不会触发 visibilitychange，
  // 没有定时器时 LCP 等观察器推送的日志会无限堆积导致内存增长
  stopReportTimer();
  reportTimer = setInterval(() => {
    reportMetrics();
  }, REPORT_INTERVAL);

  // eslint-disable-next-line no-console
  console.info('[PerformanceMonitor] Web端性能监控已初始化', {
    enabled: options.enabled,
    sampleRate: `${options.sampleRate * 100}%`,
  });
}

/**
 * 获取当前性能指标（供调试使用）
 */
export function getCurrentMetrics(): {
  cls: number;
  longTaskCount: number;
  pending: number;
} {
  return {
    cls: clsValue,
    longTaskCount,
    pending: pendingLogs.length,
  };
}

/**
 * 清空待上报的性能日志队列
 *
 * 用于用户退出登录时调用，避免：
 * 1. 退出后积累的脏数据
 * 2. 登录后批量上传大量无效日志
 */
export function clearPendingLogs(): void {
  const count = pendingLogs.length;
  pendingLogs.length = 0;
  clsValue = 0;
  longTaskCount = 0;

  if (count > 0) {
    // eslint-disable-next-line no-console
    console.info(`[Performance] 已清空 ${count} 条待上报性能日志`);
  }
}

/**
 * 获取待上报日志数量（用于调试）
 */
export function getPendingLogCount(): number {
  return pendingLogs.length;
}
